import React, { useState, useRef, useEffect } from "react";
import { View, TextInput, StyleSheet } from "react-native";
import { useTranslation } from "../../../../../i18n/useTranslation";
import PinInputComponent from "../../../common/steps/PinInputComponent";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";

type EnterPinStepProps = {
  attemptsLeft?: number;
  locked?: boolean;
  showAttemptsWarning?: boolean;
  apiError?: string;
  resetSignal?: number;
  onContinue?: () => void;
  testID?: string;
};

export default function EnterPinStep({ attemptsLeft, locked, showAttemptsWarning = true, apiError, resetSignal = 0, onContinue, testID }: EnterPinStepProps) {
  const { t } = useTranslation();
  const [pin, setPin] = useState(["", "", "", ""]);
  const inputs = useRef<(TextInput | null)[]>([]);
  const setConsumerPin = useSessionStore((s: any) => s.setConsumerPin);
  const isNewConsumer = useSessionStore((s: SessionState) => (s as any).isNewConsumer);

  // Watch for reset signal to clear PIN inputs
  useEffect(() => {
    setPin(["", "", "", ""]);
    if (locked) {
      inputs.current.forEach((input) => {
        try { (input as any)?.blur?.(); } catch {}
      });
      return;
    }
    requestAnimationFrame(() => {
      try { (inputs.current[0] as any)?.focus?.(); } catch {}
    });
  }, [resetSignal, locked]);

  const handleChange = (text: string, index: number) => {
    // Validate that text contains only digits using string methods (avoid ReDoS)
    // Check each character to ensure it's a digit (0-9) without using regex
    const isValid = text.length === 0 || text.split('').every(char => {
      const charCode = char.charCodeAt(0);
      return charCode >= 48 && charCode <= 57; // '0' is 48, '9' is 57
    });
    
    if (isValid) {
      const newPin = [...pin];
      newPin[index] = text;
      setPin(newPin);
      try { setConsumerPin?.(newPin.join("")); } catch {}
      // Move to next box automatically
      if (text && index < 3) {
        inputs.current[index + 1]?.focus();
      }
      // Backspace handling
      if (!text && index > 0) {
        inputs.current[index - 1]?.focus();
      }

      if (newPin.every(d => d !== ""))  {
        console.log('PIN CONTINUE====>', text, index)
        onContinue?.()
      }
    }
  };

  return (
    <View style={styles.container} testID={testID}>
      <PinInputComponent
          title={t("PIN_INPUT_TITLE")}
          subTitle={t("PIN_INPUT_SUBTITLE")}
          pin={pin}
          handleChange={handleChange}
          inputsRef={inputs}
          attemptsLeft={attemptsLeft}
          locked={locked}
          showAttemptsWarning={showAttemptsWarning}
          apiError={apiError}
        />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    justifyContent: "flex-start",
    marginTop: 16,
    marginBottom: 40,
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    marginBottom: 6,
    color: "#111827",
  },
  subtitle: {
    fontSize: 14,
    color: "#6B7280",
    marginBottom: 20,
  },
  pinContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  pinInput: {
    width: 60,
    height: 60,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 12,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: "#F9FAFB",
    marginRight: 24
  },
  error: {
    color: "red",
    fontSize: 12,
    marginTop: 8,
  },
});
